import type { ReviewEntryQuestion } from '../../../types/reviewEntryQuestion'
import {
  isAlertQuestion,
  isHighQuestion,
  isLowQuestion,
  isMediumQuestion,
} from '../../../utils/reviewEntryQuestionSeverity'
import type { QuestionHoverContent, QuestionHoverLabels } from '../types'

interface GetQuestionHoverContentProps {
  labels: QuestionHoverLabels
  questions: ReviewEntryQuestion[]
}

const humanizeToken = (value: string): string =>
  value
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase())

const getQuestionText = (question: ReviewEntryQuestion): string | undefined =>
  question.content?.trim() || question.message?.trim()

const getTagLabel = (question: ReviewEntryQuestion): string | undefined => {
  if (question.suggestedValue) {
    return question.suggestedValue
  }

  if (question.title) {
    return question.title
  }

  if (question.id) {
    return humanizeToken(question.id)
  }

  return undefined
}

const getHeaderTitle = (questions: ReviewEntryQuestion[], labels: QuestionHoverLabels): string | undefined => {
  const suggestionQuestion = questions.find((question) => question.suggestionType)

  if (suggestionQuestion?.suggestionType) {
    return labels.suggestionFor(humanizeToken(suggestionQuestion.suggestionType))
  }

  if (questions.some(isHighQuestion)) {
    return labels.levelHigh
  }

  if (questions.some(isMediumQuestion)) {
    return labels.levelMedium
  }

  if (questions.some(isLowQuestion)) {
    return labels.levelLow
  }

  return undefined
}

const getMessage = (questions: ReviewEntryQuestion[], labels: QuestionHoverLabels): string | undefined => {
  const messages = [...new Set(questions.map(getQuestionText).filter(Boolean))] as string[]

  if (messages.length) {
    return messages.join('\n\n')
  }

  return questions.length > 0 ? labels.fallbackMessage : undefined
}

export const getQuestionHoverContent = ({
  labels,
  questions,
}: GetQuestionHoverContentProps): QuestionHoverContent | undefined => {
  if (!questions.length) {
    return undefined
  }

  const tags = questions.flatMap((question) => {
    const label = getTagLabel(question)

    if (!label) {
      return []
    }

    const isAlert = isAlertQuestion(question)

    return [
      {
        color: isAlert ? ('red' as const) : ('yellow' as const),
        icon: isAlert ? ('circleAlert' as const) : ('circleQuestionMark' as const),
        label,
      },
    ]
  })

  const hasSuggestion = questions.some((question) => question.suggestionType)
  const hasLowQuestion = questions.some(isLowQuestion)

  return {
    headerTitle: getHeaderTitle(questions, labels),
    message: getMessage(questions, labels),
    showSparkleHeader: hasSuggestion || hasLowQuestion,
    tags,
  }
}
